import { useEffect, useState } from "react";
import { MdCategory } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { repositorioCategoriaEstoque } from "../../sync/repositories/almoxarifado";

const AJUDA = {
  titulo: "Cadastro de Categorias de Estoque",
  descricao:
    "Categorias agrupam os itens do almoxarifado (ex.: Insumos, Peças, Combustíveis). Elas são usadas para organizar e filtrar o estoque, e servem de base para as subcategorias.",
  secoes: [
    {
      titulo: "Dados da Categoria",
      campos: [
        {
          nome: "Nome da Categoria",
          obrigatorio: true,
          explicacao:
            "Nome curto e claro que identifica o grupo de itens (ex.: 'Fertilizantes', 'Ferramentas'). Evite nomes muito genéricos como 'Outros'.",
        },
      ],
    },
  ],
  dicas: [
    "Antes de criar uma categoria nova, verifique na lista se já não existe uma parecida — isso evita duplicidade e itens espalhados em categorias diferentes.",
    "Uma categoria com subcategorias vinculadas não pode ser excluída — remova ou realoque as subcategorias primeiro.",
  ],
};

export default function GestaoCategorias() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [categoriasFiltradas, setCategoriasFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: ""
  });
  
  const [formData, setFormData] = useState({
    nome: ""
  });

  useEffect(() => {
    carregarCategorias();
  }, []);

  const carregarCategorias = async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioCategoriaEstoque.listar();

      const dados = response.data.results || response.data;
      setCategorias(dados);
      setCategoriasFiltradas(dados);
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
      alert("Erro ao carregar lista de categorias");
    } finally {
      setCarregandoLista(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim()) {
      alert("Preencha o nome da categoria.");
      return;
    }
    
    setLoading(true);

    try {
      if (editando) {
        // Editar categoria existente
        const response = await repositorioCategoriaEstoque.atualizar(editandoId, formData);
        if (response.status === 200) {
          alert("Categoria atualizada com sucesso!");
          handleCancel();
          carregarCategorias();
        }
      } else {
        // Cadastrar nova categoria
        const response = await repositorioCategoriaEstoque.criar(formData);
        if (response.status === 201 || response.status === 200) {
          alert("Categoria cadastrada com sucesso!");
          handleCancel();
          carregarCategorias();
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (categoria) => {
    setFormData({
      nome: categoria.nome || ""
    });
    setEditando(true);
    setEditandoId(categoria.id);
    setMostrarFormulario(true);
    
    // Scroll para o topo para ver o formulário
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovoCategoria = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({ nome: "" });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta categoria? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioCategoriaEstoque.excluir(id);
      alert("Categoria excluída com sucesso!");
      carregarCategorias();
      
      // Se estava editando esta categoria, limpar formulário
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir categoria. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...categorias];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(c => 
        c.nome?.toLowerCase().includes(buscaLower) ||
        c.id?.toString().includes(buscaLower)
      );
    }
    
    setCategoriasFiltradas(filtrados);
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFiltros({ busca: "" });
    setCategoriasFiltradas(categorias);
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Categorias <MdCategory size={38}/></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoCategoria}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Categoria
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição - Aparece apenas quando necessário */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Categoria" : "Cadastrar Nova Categoria"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div className="flex flex-col w-96">
                  <label className="text-sm font-medium text-gray-700 mb-1">Nome da Categoria *</label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Digite o nome da categoria..."
                    required
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Código ou Nome</label>
                  <input
                    type="text"
                    placeholder="Digite o código ou nome da categoria..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Categorias */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {categoriasFiltradas.length} categoria(s) encontrada(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoriasFiltradas.map((categoria) => (
                        <tr key={categoria.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{categoria.id}</td>
                          <td className="p-3 text-sm font-medium">{categoria.nome}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(categoria)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(categoria.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Excluir"
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      
                      {categoriasFiltradas.length === 0 && (
                        <tr>
                          <td colSpan="3" className="p-8 text-center text-gray-500">
                            {filtros.busca 
                              ? "Nenhuma categoria encontrada com os filtros selecionados."
                              : "Nenhuma categoria cadastrada."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}